import { DxDataGridColumn } from "../models/DxDataGridColumn";
import { FiscalPeriod, ProductionDeterminantHistory, ProductionDeterminantListing } from "../models/ProductionDeterminantListing";
import { Constants, FieldKeyNames } from "../constants";
import { Utilities } from "../utils/utilities";

export const customGroupOrder = ["Power", "Natural Gas", "Diesel Fuel", "Exchange Rate"]; // your desired order

export const generateColumns = (data: ProductionDeterminantListing) => {
  return [
    {
      dataField: "EmptyDeterminant",
      caption: "",
      fixed: true,
      fixedPosition: "left",
      cssClass: "fixed-column-style",
      columns: [
        {
          dataField: FieldKeyNames.Determinant,
          caption: FieldKeyNames.Determinant,
          fixed: true,
          fixedPosition: "left",
          cssClass: "fixed-column-style",
        },
      ],
    },
    {
      dataField: "EmptySource",
      caption: "",
      fixed: true,
      fixedPosition: "left",
      cssClass: "fixed-column-style",
      columns: [
        {
          dataField: FieldKeyNames.Source,
          caption: FieldKeyNames.Source,
          fixed: true,
          fixedPosition: "left",
          cssClass: "fixed-column-style",
        },
      ],
    },
    ...generateDynamicColumns(data),
  ] as DxDataGridColumn[];
};

export const generateDataSource = (data: ProductionDeterminantListing) => {
  var result = [] as any[];
  var index = 0;
  data.groups.forEach((group) => {
    if (group.showPlantColumn) {
      result.push(generatePlantRowData(data.fiscalPeriods, group.categoryName, index--));
    }

    group.determinants.forEach((determinant) => {
      var row = {
        Determinant: determinant.determinantDisplayAs,
        Source: determinant.determinantPlant,
        CategoryName: group.categoryName,
        sortIndex: index--,
        determinantObject: determinant,
      } as any;
      determinant.determinantPeriods.forEach((period) => {
        row[period.fiscalPeriod.fiscalYear + period.fiscalPeriod.calendarName] = getDeterminantPeriodValue(
          determinant.determinantName,
          period.value,
          determinant.decimalsToDisplay
        );
      });

      result.push(row);
    });
  });

  return result;
};

const generatePlantRowData = (fiscalPeriods: FiscalPeriod[], categoryName: string, sortIndex: number) => {
  var row: any = {
    Determinant: Constants.PowerBillingPeriod,
    Source: "",
    CategoryName: categoryName,
    sortIndex,
  };

  fiscalPeriods.forEach((fiscalPeriod) => {
    row[fiscalPeriod.fiscalYear + fiscalPeriod.calendarName] = getPowerBillingMonth(fiscalPeriod.fiscalMonth);
  });

  return row;
};

const generateDynamicColumns = (data: ProductionDeterminantListing) => {
  var currentFY = getCurrentFY();
  var currentMonth = getCurrentMonth();

  var result = data.fiscalPeriods.map((fiscalPeriod) => {
    var cssClass = isCurrentMonth(fiscalPeriod.fiscalYear, fiscalPeriod.calendarMonth, currentFY, currentMonth) ? "current-month-style" : "";
    return {
      dataField: fiscalPeriod.fiscalYear.toString() + fiscalPeriod.fiscalMonth.toString(),
      caption: "FY " + fiscalPeriod.fiscalYear,
      cssClass,
      columns: [
        {
          dataField: fiscalPeriod.fiscalYear + fiscalPeriod.calendarName,
          caption: fiscalPeriod.calendarName,
          cssClass,
          fiscalPeriod,
        } as DxDataGridColumn,
      ],
    };
  }) as DxDataGridColumn[];

  return result;
};

export const getProductionIndicesSearchRange = (
  currentFY: number,
  currentMonth: number,
  offsetYearsBack: number | undefined = undefined,
  offsetYearsForward: number | undefined = undefined
) => {
  if (offsetYearsBack === undefined) offsetYearsBack = 0;
  if (offsetYearsForward === undefined) offsetYearsForward = 0;

  // Months offset from 0, so 8 = August, the 9th month
  // Set from date to the current fiscal year - 2 years
  var from = new Date(currentFY - 2 - offsetYearsBack, 8, 30, 0, 0, 0, 0);

  // Set to date to the current fiscal year
  var to = new Date(currentFY + offsetYearsForward, 8, 30, 0, 0, 0, 0);

  // If the month is any month other than August or September, add another year
  if (currentMonth === 7 || currentMonth === 8) to.setFullYear(to.getFullYear() + 1);

  // Return the range
  return {
    From: from.toISOString(),
    To: to.toISOString(),
  };
};

export const getCurrentFY = () => {
  var now = new Date();
  now.setMonth(now.getMonth() + 3);
  return now.getFullYear();
};

export const getCurrentMonth = () => {
  var now = new Date();
  return now.getMonth();
};

const getPowerBillingMonth = (month: number) => {
  switch (month) {
    case 1:
      return "AUG";
    case 2:
      return "SEP";
    case 3:
      return "OCT";
    case 4:
      return "NOV";
    case 5:
      return "DEC";
    case 6:
      return "JAN";
    case 7:
      return "FEB";
    case 8:
      return "MAR";
    case 9:
      return "APR";
    case 10:
      return "MAY";
    case 11:
      return "JUN";
    case 12:
      return "JUL";
  }
};

const getDeterminantPeriodValue = (determinantName: string, value: string, decimalsToDisplay: number) => {
  if (Utilities.isNullOrEmptyString(value) && determinantName === Constants.ExchangeRateUSCN) {
    return Constants.GetCurrentRate;
  }

  if (Utilities.isNullOrEmptyString(value) && determinantName !== Constants.ExchangeRateUSCN) {
    return Constants.EmptyValue;
  }

  return `${Constants.CurrencyDollar}${parseFloat(value).toFixed(decimalsToDisplay)}`;
};

const isCurrentMonth = function (fiscalyear: number, calendarMonth: number, currentFY: number, currentMonth: number) {
  return fiscalyear === currentFY && calendarMonth === currentMonth + 1;
};

export const generatePostObjFormData = (formData: any, determinant: ProductionDeterminantHistory) => {
  var updatedDeterminant = Utilities.dataClone(determinant) as ProductionDeterminantHistory;
  updatedDeterminant.determinantPeriods.forEach((period) => (period.dirty = false));

  Object.keys(formData).forEach((key) => {
    if (key === FieldKeyNames.Determinant || key === FieldKeyNames.Source) {
      return;
    }

    var fiscalYear = key.substring(0, 4);
    var calendarName = key.substring(4);
    for (let period of updatedDeterminant.determinantPeriods) {
      if (period.fiscalPeriod.fiscalYear.toString() === fiscalYear && period.fiscalPeriod.calendarName === calendarName) {
        period.dirty = period.value.toString() !== formData[key].toString();
        period.value = formData[key];
        break;
      }
    }
  });

  return updatedDeterminant;
};

export const validateDeterminantIsDirty = (formData: any, determinant: ProductionDeterminantHistory) => {
  for (let formDataKey of Object.keys(formData)) {
    var fiscalYear = formDataKey.substring(0, 4);
    var calendarName = formDataKey.substring(4);
    for (let period of determinant.determinantPeriods) {
      if (period.fiscalPeriod.fiscalYear.toString() === fiscalYear && period.fiscalPeriod.calendarName === calendarName) {
        if (period.value.toString() !== formData[formDataKey].toString()) {
          return true;
        }
      }
    }
  }

  return false;
};
